<script setup lang="ts">
import type { Button } from './Button.vue'
import type { Image } from '~/types'

interface Props {
  anchor?: string
  buttons?: Button[]
  description?: string
  image?: Image
  title?: string
  showGradient?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  buttons: () => [],
})

const hasButtons = computed<boolean>(() => props.buttons && props.buttons.length > 0)
</script>

<template>
  <SectionWrapper
    :anchor
    class="hero-w-buttons"
  >
    <div
      v-if="image"
      class="hero-w-buttons__background"
      :class="{ 'hero-w-buttons__background--with-overlay': showGradient }"
    >
      <UIImage
        :image
        class="hero-w-buttons__image"
      />
    </div>
    <Wrapper>
      <div class="hero-w-buttons__content">
        <UIDynamicHeading
          v-if="title"
          level="2"
          as="h2"
          class="hero-w-buttons__title"
        >
          {{ title }}
        </UIDynamicHeading>
        <p
          v-if="description"
          class="hero-w-buttons__description"
        >
          {{ description }}
        </p>
        <div
          v-if="hasButtons"
          class="hero-w-buttons__buttons"
        >
          <ContentBlockHeroWithButtonsButton
            v-for="(button, index) in buttons"
            :key="`hero-with-buttons-button-${index}`"
            v-bind="button"
          />
        </div>
      </div>
    </Wrapper>
  </SectionWrapper>
</template>

<style lang="scss" scoped>
.hero-w-buttons {
  @apply relative z-[1] bg-primary flex flex-col justify-center mb-[40px];
  @apply md:h-[420px];
  @apply lg:mb-[60px] lg:h-[490px];
  @apply xl:h-[560px];
  @apply 2xl:h-[630px];

  &__background {
    @apply relative top-0 left-0 w-full h-auto aspect-video -z-[1];
    @apply md:h-full md:aspect-auto md:absolute;
    @apply before:absolute before:hidden before:h-full before:w-full before:inset-0 before:z-[1] before:bg-primary/70;

    &--with-overlay {
      @apply md:before:block;
    }
  }

  &__image {
    @apply w-full h-full object-cover object-top;
  }

  &__content {
    @apply flex flex-col items-center justify-center text-center gap-[15px] py-[30px];
    @apply md:gap-[30px];
  }

  &__title {
    @apply text-white;
  }

  &__description {
    @apply text-[16px] text-white;
    @apply md:text-[20px];
  }

  &__buttons {
    @apply flex items-center justify-center flex-wrap gap-[10px];
    @apply lg:gap-[20px];
  }
}
</style>
